<script setup lang="ts">
import { Cluster, MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { computed, nextTick, onUnmounted, shallowRef, watch } from "vue";
import { useMap } from "../composable/google-map";
import { useMarkers, type MarkerOptions } from "../composable/markers";
import { useViewportMarkers } from "../composable/viewport-markers";

const props = defineProps<{
	maxZoom?: number;
	minZoom?: number;
	markers: MarkerOptions[];
}>();

const emit = defineEmits<{
	"click:cluster": [google.maps.MapMouseEvent, Cluster, google.maps.Map];
	"click:marker": [google.maps.MapMouseEvent, MarkerOptions, google.maps.Map];
	"mouseover:marker": [google.maps.MapMouseEvent, MarkerOptions, google.maps.Map];
	"mouseout:marker": [google.maps.MapMouseEvent, MarkerOptions, google.maps.Map];
}>();

defineSlots<{
	overlay(slotProps: { marker: MarkerOptions }): void;
}>();

const map = useMap();
const cluster = shallowRef<MarkerClusterer>();

const mapMarkers = useMarkers(() => props.markers, {
	click(evt, marker) {
		emit("click:marker", evt, marker, map.value!);
	},
	mouseover(evt, marker) {
		emit("mouseover:marker", evt, marker, map.value!);
	},
	mouseout(evt, marker) {
		emit("mouseout:marker", evt, marker, map.value!);
	},
});
const viewportMarkers = useViewportMarkers(true, mapMarkers);

let markersInCluster = new Map<string, google.maps.Marker>();

const markersOnMap = computed(() => {
	return viewportMarkers.value.filter((marker) => marker.getMap() != null);
});

function markerOptsFromMarker(marker: google.maps.Marker) {
	return {
		id: marker.get("id"),
		position: marker.getPosition()!,
		meta: marker.get("meta"),
		clickable: marker.getClickable(),
		draggable: marker.getDraggable(),
		icon: marker.getIcon(),
	};
}

function updateMarkerInCluster() {
	if (cluster.value == null) {
		return;
	}

	const markers = new Map(
		viewportMarkers.value.map((marker) => [marker.get("id") as string, marker])
	);

	const toAdd: google.maps.Marker[] = [];
	const toRemove: google.maps.Marker[] = [];

	// Look for marker that we need to remove
	for (const [id, marker] of markersInCluster) {
		if (!markers.has(id)) {
			toRemove.push(marker);
		}
	}

	// Look for marker that we need to add
	for (const [id, marker] of markers) {
		if (!markersInCluster.has(id)) {
			toAdd.push(marker);
		}
	}

	if (toAdd.length === 0 && toRemove.length === 0) {
		return;
	}

	// Defer draw to the adding part
	cluster.value.removeMarkers(toRemove, true);
	cluster.value.addMarkers(toAdd, false);

	// Keep a reference to current marker in cluster, since we can't get it back outerwise.
	markersInCluster = markers;
}

watch([cluster, viewportMarkers], updateMarkerInCluster);

function clearCluster() {
	if (cluster.value) {
		cluster.value.clearMarkers();
		cluster.value.setMap(null);
	}
}

function createCluster() {
	if (map.value == null) {
		return;
	}

	const algorithm = new SuperClusterAlgorithm({
		maxZoom: props.maxZoom || 16,
	});

	cluster.value = new MarkerClusterer({
		map: map.value,
		markers: [],
		algorithm,
		onClusterClick: (...args) => emit("click:cluster", ...args),
	});
}

const stopInit = watch(
	map,
	() => {
		if (!map.value) return;

		nextTick(() => stopInit());

		if (map.value.getZoom() == null) {
			google.maps.event.addListenerOnce(map.value, "idle", () => {
				createCluster();
			});
		} else {
			createCluster();
		}
	},
	{ immediate: true }
);

onUnmounted(clearCluster);
</script>

<template>
	<slot />

	<template v-for="marker of markersOnMap" :key="marker.get('id')">
		<slot name="overlay" :marker="markerOptsFromMarker(marker)" />
	</template>
</template>
